// src/pages/UpdateCustomer.jsx
import React, { useEffect, useCallback, useState } from "react";
import { motion } from "framer-motion";
import { useForm, useFieldArray } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaTrash } from "react-icons/fa";
import toast from "react-hot-toast";

import { threeDecimal, twoDecimal } from "../config/helperFunctions";
import {
  useGetCustomerByIdQuery,
  useUpdateCustomerMutation,
} from "../toolkitServices/customerApi";

import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { handleDropDowns } from "../config/dropDownHelpers";
import { indianStates } from "../utils/helpers";

/* Shared UI tokens */
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const EditCustomer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  // 1: Customer, 2: Additional, 3: Permanent Address, 4: Delivery Addresses
  const [step, setStep] = useState(1);

  const { data: resp, isFetching, isError } = useGetCustomerByIdQuery(id, {
    skip: !id,
  });
  const existing = resp?.data || resp;

  const [updateCustomer, { isLoading: isSaving, error: updError, isSuccess }] =
    useUpdateCustomerMutation();

  // Branch list
  const { data: branchesRes, isLoading: branchesLoading } =
    useGetAllBranchesQuery();
  const branches = branchesRes?.data || branchesRes?.items || [];
  const branchOptions = handleDropDowns({
    key: "branch",
    branches,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger,
    reset,
    setFocus,
    control,
    watch,
    setValue,
  } = useForm({
    mode: "onBlur",
    shouldUnregister: false,
    defaultValues: {
      customerId: "",
      customerName: "",
      companyName: "",
      email: "",
      contact: "",
      adhar: "",
      pan: "",
      gst: "",
      rateOfInterest: "",
      creditPeriod: "",
      advanceAmount: "",
      balanceAmount: "",
      fineGold: "",
      fineSilver: "",
      branchId: "", // 👈 NEW
      // permanent address object
      permanentAddress: {
        addressLine1: "",
        city: "",
        pincode: "",
        state: "Maharashtra",
        country: "india",
      },
      // delivery addresses array
      deliveryAddresses: [],
      isActive: true,
    },
  });

  const deliveryValues = watch("deliveryAddresses") || [];
  const selectedBranchId = watch("branchId");

  const {
    fields: deliveryFields,
    append: appendDelivery,
    remove: removeDelivery,
  } = useFieldArray({
    control,
    name: "deliveryAddresses",
  });

  /* Prefill */
  useEffect(() => {
    if (existing && !isFetching) {
      const pa = existing.permanentAddress || {};
      const existingDelivery = Array.isArray(existing.deliveryAddresses)
        ? existing.deliveryAddresses
        : [];

      reset({
        customerId: existing.customerId || "",
        customerName: existing.customerName || "",
        companyName: existing.companyName || "",
        email: existing.email || "",
        contact: existing.contact || "",
        adhar: existing.adhar || "",
        pan: existing.pan || "",
        gst: existing.gst || "",
        rateOfInterest: existing.rateOfInterest ?? "",
        creditPeriod: existing.creditPeriod ?? "",
        advanceAmount: existing.advanceAmount ?? "",
        balanceAmount: existing.balanceAmount ?? "",
        fineGold: existing.fineGold ?? "",
        fineSilver: existing.fineSilver ?? "",
        branchId: existing.branchId || "", // 👈 NEW
        permanentAddress: {
          addressLine1: pa.addressLine1 || existing.addressLine1 || "",
          city: pa.city || existing.city || "",
          pincode: pa.pincode || existing.pincode || "",
          state: pa.state || existing.state || "Maharashtra",
          country: pa.country || existing.country || "india",
        },
        deliveryAddresses:
          existingDelivery.length > 0
            ? existingDelivery.map((a) => ({
                addressLine1: a.addressLine1 || "",
                city: a.city || "",
                pincode: a.pincode || "",
                state: a.state || "Maharashtra",
                country: a.country || "india",
                isDefault: !!a.isDefault,
              }))
            : [],
        isActive:
          typeof existing.isActive === "boolean" ? existing.isActive : true,
      });
      setStep(1);
      requestAnimationFrame(() => setFocus("customerId"));
    }
  }, [existing, isFetching, reset, setFocus]);

  /* Step focus */
  useEffect(() => {
    const idRAF = requestAnimationFrame(() => {
      if (step === 1) setFocus("customerId");
      if (step === 2) setFocus("rateOfInterest");
      if (step === 3) setFocus("permanentAddress.addressLine1");
      if (step === 4 && deliveryFields.length > 0) {
        setFocus("deliveryAddresses.0.addressLine1");
      }
    });
    return () => cancelAnimationFrame(idRAF);
  }, [step, setFocus, deliveryFields.length]);

  /* Keyboard shortcuts */
  const nextStep = useCallback(async () => {
    const ok = await trigger();
    if (ok) setStep((s) => (s < 4 ? s + 1 : s));
  }, [trigger]);

  const prevStep = useCallback(() => {
    setStep((s) => (s > 1 ? s - 1 : s));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
      const isBack = (e.ctrlKey || e.metaKey) && e.key === "Backspace";
      const isPrev = e.altKey && e.key === "ArrowLeft";

      if (isSave) {
        e.preventDefault();
        if (step === 4) handleSubmit(onSubmit)();
        else nextStep();
      }
      if (isBack) {
        e.preventDefault();
        navigate(-1);
      }
      if (isPrev) {
        e.preventDefault();
        prevStep();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [step, handleSubmit, nextStep, prevStep, navigate]);

  /* Errors / Success */
  useEffect(() => {
    if (isError) {
      toast.error("Failed to load customer");
    }
  }, [isError]);

  useEffect(() => {
    if (updError) {
      const msg =
        updError?.data?.msg ||
        updError?.error ||
        "Failed to update customer. Please try again.";
      toast.error(msg, { position: "top-center" });
    }
  }, [updError]);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Customer updated!", { position: "top-center" });
      navigate("/members/customer-list", { replace: true });
    }
  }, [isSuccess, navigate]);

  const handleSetDefaultDelivery = (index) => {
    const current = (watch("deliveryAddresses") || []).map((addr, i) => ({
      ...addr,
      isDefault: i === index,
    }));
    setValue("deliveryAddresses", current, { shouldDirty: true });
  };

  /* Submit */
  const onSubmit = async (data) => {
    const rawDelivery = Array.isArray(data.deliveryAddresses)
      ? data.deliveryAddresses
      : [];

    let deliveryAddresses = rawDelivery.map((a) => ({
      addressLine1: a.addressLine1 || "",
      city: a.city || "",
      pincode: a.pincode || "",
      state: a.state || "Maharashtra",
      country: a.country || "india",
      isDefault: !!a.isDefault,
    }));

    if (
      deliveryAddresses.length > 0 &&
      !deliveryAddresses.some((a) => a.isDefault)
    ) {
      deliveryAddresses[0].isDefault = true;
    } else if (deliveryAddresses.length > 0) {
      // ensure only one true
      let found = false;
      deliveryAddresses = deliveryAddresses.map((a) => {
        if (a.isDefault && !found) {
          found = true;
          return { ...a, isDefault: true };
        }
        return { ...a, isDefault: false };
      });
      if (!found) deliveryAddresses[0].isDefault = true;
    }

    const permanentAddress = {
      addressLine1: data.permanentAddress?.addressLine1 || "",
      city: data.permanentAddress?.city || "",
      pincode: data.permanentAddress?.pincode || "",
      state: data.permanentAddress?.state || "Maharashtra",
      country: data.permanentAddress?.country || "india",
    };

    // Branch name from dropdown / existing
    const branchOpt = branchOptions.find(
      (b) => String(b.value) === String(data.branchId)
    );
    const branchName =
      branchOpt?.label || existing?.branchName || "";

    const payload = {
      customerName: data.customerName,
      shortName: data.shortName,
      companyName: data.companyName,
      email: data.email,
      contact: data.contact,
      // customerId backend pe change nahi kar rahe, disabled hi hai
      adhar: data.adhar,
      pan: data.pan,
      gst: data.gst,
      rateOfInterest: twoDecimal(data?.rateOfInterest),
      creditPeriod: data?.creditPeriod,
      advanceAmount: twoDecimal(data?.advanceAmount),
      balanceAmount: twoDecimal(data?.balanceAmount),
      fineGold: threeDecimal(data?.fineGold),
      fineSilver: threeDecimal(data?.fineSilver),
      permanentAddress,
      deliveryAddresses,
      isActive: data.isActive,
      branchId: data.branchId || null,
      branchName: branchName || "",
    };

    try {
      await updateCustomer({ id, ...payload }).unwrap();
    } catch {
      // toast handled in effect
    }
  };

  const loading = isSaving || isSubmitting || isFetching;

  return (
    <div className="px-4 py-4">
      {/* Header */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={"/members/customer-list"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Customer List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>

          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
                  Edit Customer
                </h1>
                <p className="text-xs text-gray-500">
                  Tips:{" "}
                  <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
                  <kbd className="px-1 bg-gray-100 rounded border">S</kbd>{" "}
                  save/next,{" "}
                  <kbd className="px-1 bg-gray-100 rounded border ml-1">
                    Ctrl
                  </kbd>
                  <kbd className="px-1 bg-gray-100 rounded border">
                    Back Spc
                  </kbd>{" "}
                  back,{" "}
                  <kbd className="px-1 bg-gray-100 rounded border ml-1">
                    Alt
                  </kbd>
                  <kbd className="px-1 bg-gray-100 rounded border">←</kbd> prev
                  step
                </p>
              </div>
              <div className="hidden sm:block pr-2">
                <span className="text-xs text-gray-500">
                  Step {step} of 4
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
        onAnimationComplete={() =>
          requestAnimationFrame(() =>
            setFocus(
              step === 1
                ? "customerId"
                : step === 2
                ? "rateOfInterest"
                : step === 3
                ? "permanentAddress.addressLine1"
                : deliveryFields.length > 0
                ? "deliveryAddresses.0.addressLine1"
                : "permanentAddress.addressLine1"
            )
          )
        }
      >
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form className="p-5" onSubmit={(e) => e.preventDefault()}>
          {/* STEP 1 — Customer Details + Branch */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Branch */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Branch <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("branchId", {
                    required: "Branch is required",
                  })}
                  className={`${baseInput} ${
                    errors.branchId ? errRing : okRing
                  }`}
                  disabled={branchesLoading}
                >
                  <option value="">
                    {branchesLoading ? "Loading branches…" : "Select branch"}
                  </option>
                  {branchOptions.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
                {errors.branchId && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.branchId.message}
                  </p>
                )}
              </div>
              {/* Customer Id */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Customer Id/Phone <span className="text-red-500">*</span>
                </label>
                <input
                  disabled
                  {...register("customerId", {
                    setValueAs: (v) =>
                      (v ?? "").toString().replace(/\D/g, "").slice(0, 10),
                    pattern: {
                      value: /^\d{10}$/,
                      message: "Enter exactly 10 digits",
                    },
                    minLength: { value: 10, message: "Must be 10 digits" },
                    maxLength: { value: 10, message: "Must be 10 digits" },
                  })}
                  placeholder="Enter 10-digit mobile"
                  className={`${baseInput} ${
                    errors.customerId ? errRing : okRing
                  } bg-gray-100 cursor-not-allowed`}
                />
                {errors.customerId?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.customerId.message}
                  </p>
                )}
              </div>

            

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Customer Name
                </label>
                <input
                  {...register("customerName")}
                  placeholder="Enter customer name"
                  className={`${baseInput} ${okRing}`}
                />
              </div>
</div>
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                <input
                  {...register("companyName")}
                  placeholder="Enter company name"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  {...register("email")}
                  placeholder="Enter email"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Contact
                </label>
                <input
                  {...register("contact", {
                    validate: (value) =>
                      value === "" ||
                      /^\d{10}$/.test(value) ||
                      "Contact must be exactly 10 digits",
                  })}
                  placeholder="Enter contact"
                  className={`${baseInput} ${
                    errors.contact ? errRing : okRing
                  }`}
                />
                {errors.contact?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.contact.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Aadhar
                </label>
                <input
                  {...register("adhar", {
                    validate: (value) =>
                      value === "" ||
                      /^\d{12}$/.test(value) ||
                      "Aadhar must be 12 digits",
                  })}
                  placeholder="Enter aadhar"
                  className={`${baseInput} ${
                    errors.adhar ? errRing : okRing
                  }`}
                />
                {errors.adhar?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.adhar.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  PAN
                </label>
                <input
                  {...register("pan", {
                    validate: (value) =>
                      value === "" ||
                      /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(
                        (value || "").toUpperCase()
                      ) ||
                      "PAN must be in format AAAAA9999A",
                  })}
                  placeholder="Enter PAN"
                  className={`${baseInput} ${
                    errors.pan ? errRing : okRing
                  }`}
                />
                {errors.pan?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.pan.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  GST
                </label>
                <input
                  {...register("gst", {
                    validate: (value) =>
                      value === "" ||
                      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
                        (value || "").toUpperCase()
                      ) ||
                      "GST number must be a valid 15-character alphanumeric code",
                  })}
                  placeholder="Enter GST"
                  className={`${baseInput} ${
                    errors.gst ? errRing : okRing
                  }`}
                />
                {errors.gst?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.gst.message}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* STEP 2 — Additional Info */}
          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Rate of interest
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register("rateOfInterest")}
                  placeholder="Enter rate of interest"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Credit period
                </label>
                <input
                  type="number"
                  {...register("creditPeriod")}
                  placeholder="Enter credit period"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Advance amount
                </label>
                <input
                  {...register("advanceAmount")}
                  placeholder="Enter advance amount"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Balance amount
                </label>
                <input
                  {...register("balanceAmount")}
                  placeholder="Enter balance amount"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Fine gold
                </label>
                <input
                  {...register("fineGold")}
                  placeholder="Enter fine gold"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Fine silver
                </label>
                <input
                  {...register("fineSilver")}
                  placeholder="Enter fine silver"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              <div className="md:col-span-2 pt-2">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    {...register("isActive")}
                  />
                <span className="text-sm text-[#4b3d91] font-medium">
                    Active
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* STEP 3 — Permanent Address */}
          {step === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Permanent Address Line 1
                </label>
                <input
                  {...register("permanentAddress.addressLine1")}
                  placeholder="Enter street / area"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  {...register("permanentAddress.city")}
                  placeholder="Enter city"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Pincode
                </label>
                <input
                  {...register("permanentAddress.pincode", {
                    validate: (value) =>
                      value === "" ||
                      /^\d{6}$/.test(value) ||
                      "Pincode must be a 6-digit number",
                  })}
                  placeholder="Enter pincode"
                  className={`${baseInput} ${
                    errors?.permanentAddress?.pincode ? errRing : okRing
                  }`}
                />
                {errors?.permanentAddress?.pincode && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.permanentAddress.pincode.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("permanentAddress.state", {
                    required: "State is required",
                  })}
                  className={`${baseInput} ${
                    errors?.permanentAddress?.state ? errRing : okRing
                  }`}
                >
<option value="">Select State</option>

                  {indianStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}                </select>
                {errors?.permanentAddress?.state && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.permanentAddress.state.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("permanentAddress.country", {
                    required: "Country is required",
                  })}
                  className={`${baseInput} ${
                    errors?.permanentAddress?.country ? errRing : okRing
                  }`}
                >
                  <option value="india">India</option>
                </select>
                {errors?.permanentAddress?.country && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.permanentAddress.country.message}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* STEP 4 — Delivery Addresses */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-[#4b3d91]">
                  Delivery Addresses (max 5)
                </h2>
                <button
                  type="button"
                  onClick={() =>
                    appendDelivery({
                      addressLine1: "",
                      city: "",
                      pincode: "",
                      state: "Maharashtra",
                      country: "india",
                      isDefault: deliveryFields.length === 0,
                    })
                  }
                  disabled={deliveryFields.length >= 5}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#9481ce] text-white hover:bg-[#7d6ec0] disabled:opacity-60"
                >
                  + Add Delivery Address
                </button>
              </div>

              {deliveryFields.length === 0 && (
                <p className="text-xs text-gray-500">
                  No delivery address added. You can still save customer with
                  only permanent address.
                </p>
              )}

              <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
                {deliveryFields.map((field, index) => (
                  <div
                    key={field.id}
                    className="border rounded-lg p-3 bg-slate-50/60 relative"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-600">
                        Address #{index + 1}
                      </span>
                      <div className="flex items-center gap-3">
                        <label className="inline-flex items-center gap-1 text-xs text-[#4b3d91] cursor-pointer">
                          <input
                            type="radio"
                            name="defaultDeliveryAddress"
                            className="h-4 w-4"
                            checked={!!deliveryValues[index]?.isDefault}
                            onChange={() => handleSetDefaultDelivery(index)}
                          />
                          <span>Set as default</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => removeDelivery(index)}
                          className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                        >
                          <FaTrash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* keep isDefault in form state */}
                    <input
                      type="hidden"
                      {...register(`deliveryAddresses.${index}.isDefault`)}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="md:col-span-2">
                        <label className="block text-[12px] text-gray-700 mb-1">
                          Address Line 1
                        </label>
                        <input
                          {...register(
                            `deliveryAddresses.${index}.addressLine1`
                          )}
                          placeholder="Street / Area"
                          className={`${baseInput} ${okRing}`}
                        />
                      </div>

                      <div>
                        <label className="block text-[12px] text-gray-700 mb-1">
                          City
                        </label>
                        <input
                          {...register(`deliveryAddresses.${index}.city`)}
                          placeholder="City"
                          className={`${baseInput} ${okRing}`}
                        />
                      </div>

                      <div>
                        <label className="block text-[12px] text-gray-700 mb-1">
                          Pincode
                        </label>
                        <input
                          {...register(
                            `deliveryAddresses.${index}.pincode`,
                            {
                              validate: (value) =>
                                value === "" ||
                                /^\d{6}$/.test(value) ||
                                "Pincode must be 6 digits",
                            }
                          )}
                          placeholder="Pincode"
                          className={`${baseInput} ${
                            errors?.deliveryAddresses?.[index]?.pincode
                              ? errRing
                              : okRing
                          }`}
                        />
                        {errors?.deliveryAddresses?.[index]?.pincode && (
                          <p className="text-xs text-red-600 mt-1">
                            {
                              errors.deliveryAddresses[index].pincode
                                .message
                            }
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-[12px] text-gray-700 mb-1">
                          State
                        </label>
                        <select
                          {...register(`deliveryAddresses.${index}.state`)}
                          className={`${baseInput} ${okRing}`}
                        >
<option value="">Select State</option>

                  {indianStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}                        </select>
                      </div>

                      <div>
                        <label className="block text-[12px] text-gray-700 mb-1">
                          Country
                        </label>
                        <select
                          {...register(`deliveryAddresses.${index}.country`)}
                          className={`${baseInput} ${okRing}`}
                        >
                          <option value="india">India</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer actions */}
          <div className="flex flex-col sm:flex-row gap-2 pt-5">
            <motion.button
              whileHover={{
                scale: loading ? 1 : 1.01,
              }}
              whileTap={{
                scale: loading ? 1 : 0.98,
              }}
              type="button"
              onClick={prevStep}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-60"
              disabled={step === 1 || loading}
              title="Previous step"
            >
              Previous
            </motion.button>

            <motion.button
              whileHover={{
                scale: loading ? 1 : 1.01,
              }}
              whileTap={{
                scale: loading ? 1 : 0.98,
              }}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (step < 4) nextStep();
                else handleSubmit(onSubmit)();
              }}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
              disabled={loading}
              title={step === 4 ? "Update (Ctrl+S)" : "Next (Ctrl+S)"}
            >
              {loading ? "Updating..." : step === 4 ? "Update (Ctrl+S)" : "Next"}
            </motion.button>

            <Link
              to="/members/customer-list"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditCustomer;
